// authService.ts
export const SECRET_KEY = import.meta.env.VITE_SECRET_KEY || "";

import { logError } from "~src/utils/logger";
interface UserData {
  id: string;
  username?: string;
  email?: string;
  [key: string]: any; // Allow for other properties
}

// Store user data separately from the token
let currentUser: UserData | null = null;

/**
 * Set the authentication token and optionally store user data
 */
export const setAuthToken = (token: string, userData?: UserData): void => {
  const encodedToken = btoa(SECRET_KEY + token);
  localStorage.setItem("auth_token", encodedToken);

  // Store user data if provided
  if (userData) {
    setUserData(userData);
  }
};

/**
 * Store user data in local storage and memory
 */
export const setUserData = (userData: UserData): void => {
  currentUser = userData;
  localStorage.setItem("user_data", JSON.stringify(userData));
};

/**
 * Clear both token and user data on logout
 */
export const logout = (): void => {
  clearAuthToken();
  clearUserData();

  // Dispatch logout event for other components to react
  window.dispatchEvent(new CustomEvent('auth:logout'));
};

/**
 * Get the authentication token
 */
export const getAuthToken = (): string | null => {
  const encodedToken = localStorage.getItem("auth_token");
  if (!encodedToken) return null;

  const decodedToken = atob(encodedToken);
  return decodedToken.substring(SECRET_KEY.length);
};

/**
 * Clear the authentication token
 */
export const clearAuthToken = (): void => {
  localStorage.removeItem("auth_token");
};

/**
 * Clear user data on logout
 */
export const clearUserData = (): void => {
  currentUser = null;
  localStorage.removeItem("user_data");
};

/**
 * Get the authentication token header for API requests
 */
export const getAuthTokenHeader = (): { Authorization: string } | object => {
  const token = getAuthToken();
  return token ? { Authorization: `Token ${token}` } : {};
};

/**
 * Get the current user ID
 */
export const getUserId = (): string | null => {
  // First try from memory
  if (currentUser?.id) {
    return currentUser.id;
  }

  // Then try from local storage
  try {
    const storedUserData = localStorage.getItem("user_data");
    if (storedUserData) {
      const userData = JSON.parse(storedUserData) as UserData;
      currentUser = userData; // Update memory cache
      return userData.id;
    }
  } catch (error) {
    logError(`Error parsing stored user data: ${error}`);
    clearUserData(); // Clear invalid data
  }

  return null;
};

/**
 * Get all user data
 */
export const getUserData = (): UserData | null => {
  // First try from memory
  if (currentUser) {
    return currentUser;
  }

  // Then try from local storage
  try {
    const storedUserData = localStorage.getItem("user_data");
    if (storedUserData) {
      const userData = JSON.parse(storedUserData) as UserData;
      currentUser = userData; // Update memory cache
      return userData;
    }
  } catch (error) {
    logError(`Error parsing stored user data: ${error}`);
    clearUserData(); // Clear invalid data
  }

  return null;
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!getAuthToken() && !!getUserId();
};

// Initialize by loading user data from localStorage when the module loads
try {
  const storedUserData = localStorage.getItem("user_data");
  if (storedUserData) {
    currentUser = JSON.parse(storedUserData);
  }
} catch (error) {
  logError(`Error initializing user data: ${error}`);
  clearUserData();
}